"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Menu, X, ChevronDown } from "lucide-react";
import { IconComponent } from "@repo/ui/components/icons";
import HeaderLangSelector from "./HeaderLangSelector";
import HeaderUserActions from "./HeaderUserActions";
import { HeaderSearch } from "./HeaderSearch";

export interface MenuTreeNode {
  _id: string;
  title?: { en?: string; mm?: string };
  url?: string;
  slug?: string;
  icon?: string;
  openInNewTab?: boolean;
  displayType?: "dropdown" | "mega";
  children?: MenuTreeNode[];
}

interface MobileMenuProps {
  primaryMenu: MenuTreeNode[];
  quickMenu?: MenuTreeNode[];
  currentLanguage: "en" | "mm";
  showSearch: boolean;
  showNavigation: boolean;
  showLanguageSelector: boolean;
  showUserMenu: boolean;
  showSignIn?: boolean;
  showSignUp?: boolean;
  brandTitle: string;
  /** Tone of the trigger row (matches the bar it sits on). */
  tone?: "dark" | "light";
}

/**
 * Epona mobile header surface. Rendered below the `lg` breakpoint —
 * the desktop chrome renders in parallel inside each variant.
 *
 * Surfaces:
 *   - Search icon → opens the shared global (Cmd+K) palette.
 *   - Hamburger → slide-in drawer with accordion primary + quick menus,
 *     language switcher and user actions.
 *
 * Adapted from the um1sf MobileMenu; restyled to Epona tokens
 * (`--font-heading`, `--color-primary`, `--color-accent`).
 */
export function MobileMenu({
  primaryMenu,
  quickMenu = [],
  currentLanguage,
  showSearch,
  showNavigation,
  showLanguageSelector,
  showUserMenu,
  showSignIn = true,
  showSignUp = true,
  brandTitle,
  tone = "light",
}: MobileMenuProps) {
  const [open, setOpen] = useState(false);

  // Body scroll-lock while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape closes the drawer.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && open) setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const menuLabel = currentLanguage === "mm" ? "မီနူး" : "Menu";
  const closeLabel = currentLanguage === "mm" ? "ပိတ်ရန်" : "Close";
  const isDark = tone === "dark";
  const triggerClass = isDark
    ? "text-white/90 hover:bg-white/10"
    : "text-foreground hover:text-[color:var(--color-primary)] hover:bg-muted/60";

  return (
    <>
      {/* Trigger row — laid out by the variant's flex parent. */}
      <div className="flex items-center gap-1">
        {showSearch && (
          <HeaderSearch
            currentLanguage={currentLanguage}
            tone={tone}
            compact
          />
        )}
        <button
          type="button"
          aria-label={menuLabel}
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors ${triggerClass}`}
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Drawer overlay + panel — mounted only when open. */}
      {open && (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label={closeLabel}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />

          <aside
            className="absolute right-0 top-0 h-full w-[86%] max-w-[22rem] bg-background shadow-2xl flex flex-col"
            style={{ borderLeft: "1px solid var(--color-border)" }}
          >
            {/* Accent strip + brand + close. */}
            <div className="bg-dental06-gradient h-1.5 w-full" />
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span
                className="dental06-heading text-base font-bold truncate"
                style={{
                  color: "var(--color-primary)",
                  fontFamily: "var(--font-heading)",
                }}
              >
                {brandTitle || menuLabel}
              </span>
              <button
                type="button"
                aria-label={closeLabel}
                onClick={() => setOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground hover:text-[color:var(--color-primary)] hover:bg-muted/60"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain">
              {showNavigation && primaryMenu.length > 0 && (
                <MenuSection
                  label={currentLanguage === "mm" ? "မီနူး" : "Menu"}
                  nodes={primaryMenu}
                  language={currentLanguage}
                  onNavigate={() => setOpen(false)}
                />
              )}
              {quickMenu.length > 0 && (
                <MenuSection
                  label={
                    currentLanguage === "mm" ? "အမြန်လင့်များ" : "Quick Links"
                  }
                  nodes={quickMenu}
                  language={currentLanguage}
                  onNavigate={() => setOpen(false)}
                  variant="muted"
                />
              )}
            </div>

            {(showLanguageSelector || showUserMenu) && (
              <div
                className="border-t border-border px-4 py-3 flex items-center justify-between gap-3 bg-muted/30"
                style={
                  {
                    ["--color-gateway-text" as any]: "var(--color-primary)",
                  } as React.CSSProperties
                }
              >
                {showLanguageSelector ? <HeaderLangSelector /> : <span />}
                {showUserMenu ? (
                  <HeaderUserActions
                    language={currentLanguage}
                    showSignIn={showSignIn}
                    showSignUp={showSignUp}
                  />
                ) : (
                  <span />
                )}
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}

function MenuSection({
  label,
  nodes,
  language,
  onNavigate,
  variant = "default",
}: {
  label: string;
  nodes: MenuTreeNode[];
  language: "en" | "mm";
  onNavigate: () => void;
  variant?: "default" | "muted";
}) {
  return (
    <div className="px-2 py-3 border-b border-border last:border-b-0">
      <div
        className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider"
        style={{
          color:
            variant === "muted"
              ? "var(--color-muted-foreground)"
              : "var(--color-accent)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {label}
      </div>
      <ul>
        {nodes.map((n) => (
          <MenuRow
            key={n._id}
            node={n}
            language={language}
            depth={0}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </div>
  );
}

function MenuRow({
  node,
  language,
  depth,
  onNavigate,
}: {
  node: MenuTreeNode;
  language: "en" | "mm";
  depth: number;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const label = pickLang(node.title, language) || node.slug || "";
  const hasChildren = (node.children?.length ?? 0) > 0;
  const indent = depth === 0 ? 0 : depth === 1 ? 12 : 24;

  if (!hasChildren) {
    return (
      <li>
        <Link
          href={node.url || "#"}
          target={node.openInNewTab ? "_blank" : undefined}
          rel={node.openInNewTab ? "noopener noreferrer" : undefined}
          onClick={onNavigate}
          className={`flex items-center gap-2 rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-muted/60 ${
            depth === 0
              ? "text-foreground font-medium"
              : "text-muted-foreground"
          }`}
          style={{ paddingLeft: 12 + indent, fontFamily: "var(--font-sans)" }}
        >
          {node.icon && (
            <IconComponent name={node.icon} size={14} className="flex-shrink-0" />
          )}
          <span className="truncate">{label}</span>
        </Link>
      </li>
    );
  }

  const hasRealUrl = !!node.url && node.url !== "#";

  return (
    <li>
      <div className="flex items-stretch rounded-md hover:bg-muted/60">
        {hasRealUrl ? (
          <Link
            href={node.url!}
            target={node.openInNewTab ? "_blank" : undefined}
            rel={node.openInNewTab ? "noopener noreferrer" : undefined}
            onClick={onNavigate}
            className={`flex flex-1 items-center gap-2 px-3 py-2.5 text-sm min-w-0 transition-colors ${
              depth === 0
                ? "text-foreground font-medium"
                : "text-muted-foreground"
            }`}
            style={{ paddingLeft: 12 + indent, fontFamily: "var(--font-sans)" }}
          >
            {node.icon && (
              <IconComponent
                name={node.icon}
                size={14}
                className="flex-shrink-0"
              />
            )}
            <span className="truncate">{label}</span>
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`flex flex-1 items-center gap-2 px-3 py-2.5 text-sm min-w-0 transition-colors text-left ${
              depth === 0
                ? "text-foreground font-medium"
                : "text-muted-foreground"
            }`}
            style={{ paddingLeft: 12 + indent, fontFamily: "var(--font-sans)" }}
            aria-expanded={open}
          >
            {node.icon && (
              <IconComponent
                name={node.icon}
                size={14}
                className="flex-shrink-0"
              />
            )}
            <span className="truncate">{label}</span>
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Collapse" : "Expand"}
          aria-expanded={open}
          className="inline-flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {open && (
        <ul className="py-1">
          {node.children!.map((c) => (
            <MenuRow
              key={c._id}
              node={c}
              language={language}
              depth={depth + 1}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function pickLang(
  text: { en?: string; mm?: string } | undefined | null,
  lang: "en" | "mm",
): string {
  if (!text) return "";
  return (text as any)[lang] || text.en || text.mm || "";
}

export default MobileMenu;
